import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"

// The right-hand drawer (layouts/drawer), after UYV-Time's drawer controller.
//
// On connect it slides the panel in, focuses the first control, locks the
// page scroll and remembers what had focus. A save inside the drawer
// re-renders the frame and connects a fresh controller; data-open on the
// frame says the drawer was already showing, so the panel appears in place
// instead of sliding in again.
//
// On close it slides the panel out, then empties the frame and hands focus
// back. Escape closes it, and Tab is kept inside the panel. When something
// inside was saved, the page behind reloads after the close, so it shows
// what changed (a request that moved group, a subdivision's row count).
export default class extends Controller {
  static targets = ["backdrop", "panel"]

  connect() {
    this.frame = this.element.closest("turbo-frame")
    this.previouslyFocused = document.activeElement
    document.body.style.overflow = "hidden"

    if (this.frame?.dataset.open === "true") {
      this.show()
      return
    }

    this.frame.dataset.open = "true"
    // Wait a frame so the browser paints the off-screen state first;
    // otherwise the transition has nothing to move from.
    requestAnimationFrame(() => {
      this.show()
      setTimeout(() => this.focusFirst(), 50)
    })
  }

  disconnect() {
    document.body.style.overflow = ""
  }

  show() {
    this.panelTarget.classList.remove("translate-x-full")
    this.backdropTarget.classList.remove("opacity-0")
  }

  close(event) {
    event?.preventDefault()
    this.panelTarget.classList.add("translate-x-full")
    this.backdropTarget.classList.add("opacity-0")

    let finished = false
    const finish = () => {
      if (finished) return
      finished = true
      delete this.frame.dataset.open
      this.frame.removeAttribute("src")
      this.frame.innerHTML = ""
      if (this.frame.dataset.saved === "true") {
        delete this.frame.dataset.saved
        Turbo.visit(window.location.href, { action: "replace" })
      } else {
        this.previouslyFocused?.focus?.()
      }
    }
    this.panelTarget.addEventListener("transitionend", finish, { once: true })
    // A reduced-motion setting can skip the transition and its event.
    setTimeout(finish, 300)
  }

  // turbo:submit-end from any form inside. Kept on the frame, which
  // outlives the controller a save's re-render replaces.
  noteSave(event) {
    if (event.detail.success) this.frame.dataset.saved = "true"
  }

  onKeydown(event) {
    if (event.key === "Escape") {
      // A control that used Escape itself keeps the drawer open, so one key
      // does not throw away a half-typed value.
      if (event.defaultPrevented) return
      this.close(event)
    } else if (event.key === "Tab") {
      this.trapTab(event)
    }
  }

  focusFirst() {
    this.focusables()[0]?.focus()
  }

  trapTab(event) {
    const focusables = this.focusables()
    if (focusables.length === 0) return

    const first = focusables[0]
    const last = focusables[focusables.length - 1]
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    }
  }

  focusables() {
    return Array.from(
      this.panelTarget.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((element) => element.offsetParent !== null)
  }
}
